import React from 'react';
import { Box, CircularProgress, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { getFeedbackUser } from '@/hooks/apiHooks';
import { imgUrlChk } from '@/utils/function';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import { styled } from '@mui/material/styles';

type SandboxLikeSummaryProps = {
  documentId: string;
  isLike: boolean;
  isLikePending?: boolean;
  disabled?: boolean;
  onLikeClick?: () => void;
  onSummaryClick?: () => void;
  sx?: SxProps<Theme>;
};

const BottomBarHeartIcon = ({ isLike }: { isLike: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M15.8883 10.9499L10.7029 16.1353C10.3123 16.5258 9.67918 16.5258 9.28865 16.1353L4.1032 10.9499C2.47602 9.32267 2.47602 6.68449 4.1032 5.0573C5.73039 3.43012 8.36857 3.43012 9.99576 5.0573C11.6229 3.43012 14.2611 3.43012 15.8883 5.0573C17.5155 6.68449 17.5155 9.32267 15.8883 10.9499Z"
      fill={isLike ? '#0066CC' : 'none'}
      stroke={isLike ? 'none' : '#888D96'}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Root container: row layout
const Container = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

// Left like toggle area: avatars + heart icon - toggles like
const LikeToggleButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
  minHeight: 32,
  padding: 0,
  boxSizing: 'border-box',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'all 0.2s',
  outline: 'none',
  '&:hover': {
    background: 'transparent',
  },
  '&:active': {
    background: 'transparent',
  },
  '&:focus-visible .heart-icon-wrapper': {
    boxShadow: '0 0 0 2px rgba(85, 89, 98, 0.12)',
  },
  '&:disabled': {
    cursor: 'default',
    opacity: 0.6,
    '&:hover': {
      background: 'transparent',
    },
  },
});

// Avatar stack with overlapping images
const AvatarStack = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  '& .avatar-image': {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '1px solid var(--dk-bg, #FFFFFF)',
    objectFit: 'cover',
  },
  '& .avatar-image + .avatar-image': {
    marginLeft: -12,
  },
});

// Heart icon wrapper (overlaps with avatar stack when avatars exist)
const HeartIconWrapper = styled('span')<{
  hasAvatars: boolean;
}>(({ hasAvatars }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  marginLeft: hasAvatars ? -12 : 0,
  boxSizing: 'border-box',
  border: '1px solid var(--dk-bg, #FFFFFF)',
  borderRadius: '999px',
  background: 'var(--dk-bg-muted, #F3F5F6)',
  flexShrink: 0,
}));

// Middle summary text area - opens comment sheet
const SummaryButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  '&:disabled': {
    cursor: 'default',
    opacity: 0.6,
  },
});

// Summary text with 16px font size per design
const LikeText = styled('span')({
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: 'var(--dk-text, #000000)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '& strong': {
    color: 'var(--dk-accent, #0066CC)',
    fontWeight: 700,
  },
});

const SandboxLikeSummary: React.FC<SandboxLikeSummaryProps> = ({
  documentId,
  isLike,
  isLikePending,
  disabled,
  onLikeClick,
  onSummaryClick,
  sx,
}) => {
  const { data, isLoading } = getFeedbackUser(documentId);

  const likeCount = Array.isArray(data) ? data.length : 0;
  const avatars = Array.isArray(data) ? data.slice(0, 4) : [];
  const hasAvatars = avatars.length > 0;

  const renderSummaryText = () => {
    if (isLoading) {
      return <LikeText>불러오는 중...</LikeText>;
    }
    if (likeCount === 0) {
      return (
        <LikeText>
          게시물에 첫번째로 <strong>좋아요</strong>를 눌러보세요.
        </LikeText>
      );
    }
    return (
      <LikeText>
        <strong>{likeCount}</strong>명의 프프인이 좋아합니다.
      </LikeText>
    );
  };

  return (
    <Box sx={sx}>
      <Container>
        {/* Left: Like toggle area (avatars + heart icon) - toggles like */}
        <LikeToggleButton
          type="button"
          disabled={disabled || isLikePending}
          onClick={onLikeClick}
          aria-label={isLike ? '좋아요 취소' : '좋아요'}
          aria-busy={isLikePending}
        >
          {hasAvatars && !isLikePending && (
            <AvatarStack>
              {avatars.map((item) => (
                <ImageWithErrorHandling
                  key={item.passportId}
                  src={imgUrlChk(item.profileImageUrl)}
                  width={32}
                  height={32}
                  alt={`${item.name} Image`}
                  className="avatar-image"
                />
              ))}
            </AvatarStack>
          )}
          <HeartIconWrapper
            className="heart-icon-wrapper"
            hasAvatars={hasAvatars && !isLikePending}
          >
            {isLikePending ? (
              <CircularProgress size={20} sx={{ color: 'var(--dk-text-secondary, #888D96)' }} />
            ) : (
              <BottomBarHeartIcon isLike={isLike} />
            )}
          </HeartIconWrapper>
        </LikeToggleButton>

        {/* Middle: Summary text area - opens comment sheet */}
        <SummaryButton
          type="button"
          disabled={disabled}
          onClick={onSummaryClick}
          aria-label="댓글 보기"
        >
          {renderSummaryText()}
        </SummaryButton>
      </Container>
    </Box>
  );
};

export default SandboxLikeSummary;
